import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import { useForm } from "react-hook-form";
import useLipaBidhaaStore from "../../../stores/lipaBidhaaStore";
import { useGetMerchantWalletBalance } from "../../../hooks/queries/useMerchantQueries";
import {
  useLipaBidhaaCharges,
  useLipaBidhaaPayment,
  useLipaBidhaaResendOtp,
  useLipaBidhaaTransaction,
  useLipaBidhaaVerification,
} from "../../../hooks/queries/useLipaBidhaaQueries";
import { usePollTransaction } from "./usePollTransaction";
import { useOtpInactivityTimer } from "../../shared/useOtpInactivityTimer";
import { useResendTimer } from "../../../hooks/useResendTimer";
import { useSetFieldError } from "../../../hooks/useSetFieldError";
import { ALIAS_VERIFICATION_STATUS } from "../config/verificationStatus";
import {
  PAYMENT_SUMMARY_DETAILS,
  PAYMENT_TYPE_CONFIG,
  PAYMENT_TYPE_FIELD_CONFIG,
  PAYMENT_TYPE_HEADER,
  PAYMENT_TYPES,
} from "../config/lipaBidhaaConfig";
import { formatKES, getDisplayValue } from "../../../utils/transactionutils";
import { toastService } from "../../../providers";
import { extractAxiosErrorMessage } from "../../../services/utils/errorUtils";
import { isInvalidOtpMessage, applyOtpBlock } from "../../shared/otpErrorUtils";
import { useLipaBidhaaOtpBlockStore } from "../../../stores/useOtpBlockStore";

function getMerchantVerificationStatus(aliasVerificationStatus) {
  if (aliasVerificationStatus === ALIAS_VERIFICATION_STATUS.VERIFIED)
    return "success";
  if (aliasVerificationStatus === ALIAS_VERIFICATION_STATUS.UNVERIFIED)
    return "error";
  return undefined;
}

function buildTransactionPayload({
  paymentType,
  alias,
  accountNumber,
  amount,
}) {
  const base = { amount: Number(amount), paymentType };
  switch (paymentType) {
    case PAYMENT_TYPES.PAYBILL:
      return { ...base, paybill: alias, accountNumber };
    case PAYMENT_TYPES.TILL_NUMBER:
      return { ...base, tillNumber: alias };
    case PAYMENT_TYPES.B2B:
      return { ...base, alias };
    default:
      return base;
  }
}

function getVerifyErrorMessage(paymentType) {
  const label =
    PAYMENT_TYPE_FIELD_CONFIG[paymentType]?.aliasLabel ?? "merchant";
  return `Please verify your ${label.toLowerCase()} number before continuing.`;
}

function buildSummaryCardProps({
  paymentTypeLabel,
  aliasLabel,
  liveAlias,
  businessName,
  merchantVerificationStatus,
  formattedAmount,
  formattedTransactionFee,
  formattedWalletBalance,
  currentStep,
  paymentRail,
}) {
  if (currentStep === 1) {
    return {
      title: PAYMENT_SUMMARY_DETAILS.paymentSummary.title,
      bannerMessage: PAYMENT_SUMMARY_DETAILS.paymentSummary.bannerMessage,
      summaryItems: [
        {
          label: "Payment Type",
          value: paymentTypeLabel,
        },
        {
          label: aliasLabel,
          value: liveAlias || "—",
          status: merchantVerificationStatus,
        },
        {
          label: "Business Name",
          value: businessName || "—",
          status: merchantVerificationStatus,
        },
        {
          label: "Amount",
          value: formattedAmount,
        },
        {
          label: "Transaction Fee",
          value: formattedTransactionFee,
        },
        {
          label: "Current Balance",
          value: formattedWalletBalance,
        },
      ],
    };
  }
  return {
    title: PAYMENT_SUMMARY_DETAILS.transactionDetails.title,
    bannerMessage: PAYMENT_SUMMARY_DETAILS.transactionDetails.bannerMessage,
    summaryItems: [
      { label: "Type", value: paymentTypeLabel },
      { label: "Recipient", value: businessName || "—", status: "success" },
      { label: "Payment Rail", value: paymentRail },
      { label: "Processing Time", value: "Instant" },
    ],
  };
}

export function usePaymentFlow(paymentType) {
  const {
    currentStep,
    aliasVerificationStatus,
    businessName,
    transactionFee,
    otpRecipient,
    transactionUuid,
    accountNumber: storedAccountNumberForResult,
    verifiedAlias,
    walletBalance: storedWalletBalance,
    remarks,
    transactionAmount: draftTransactionAmount,
  } = useLipaBidhaaStore(
    useShallow(
      ({
        currentStep,
        aliasVerificationStatus,
        businessName,
        transactionFee,
        otpRecipient,
        transactionUuid,
        accountNumber,
        verifiedAlias,
        walletBalance,
        remarks,
        transactionAmount,
      }) => ({
        currentStep,
        aliasVerificationStatus,
        businessName,
        transactionFee,
        otpRecipient,
        transactionUuid,
        accountNumber,
        verifiedAlias,
        walletBalance,
        remarks,
        transactionAmount,
      }),
    ),
  );

  const {
    alias: storedAlias,
    accountNumber: storedAccountNumber,
    transactionAmount: storedTransactionAmount,
  } = useLipaBidhaaStore.getState();

  const { watchFields } = PAYMENT_TYPE_FIELD_CONFIG[paymentType] ?? {
    watchFields: ["alias", "amount"],
  };

  const { control, trigger, watch, setError, setFocus, clearErrors } = useForm({
    defaultValues: {
      alias: storedAlias || "",
      accountNumber: storedAccountNumber || "",
      amount: storedTransactionAmount || "",
    },
    mode: "onTouched",
  });

  const setFieldError = useSetFieldError(setError, setFocus);

  useEffect(() => {
    if (aliasVerificationStatus === ALIAS_VERIFICATION_STATUS.VERIFIED) {
      clearErrors("alias");
    }
  }, [aliasVerificationStatus, clearErrors]);

  const {
    data: walletData,
    isLoading: isBalanceLoading,
    isError: isBalanceFetchError,
  } = useGetMerchantWalletBalance();
  // Null signals "not yet available" — keeps AmountField validation disabled
  // until a real value is confirmed.
  const walletBalance = walletData?.balance ?? null;
  const isBalanceError = !isBalanceLoading && isBalanceFetchError;

  const { mutate: fetchCharges, isPending: isLoadingCharges } =
    useLipaBidhaaCharges();

  const { mutate: submitPayment, isPending: isRequestingPayment } =
    useLipaBidhaaPayment();

  const { mutate: verifyOtp, isPending: isAuthorizing } =
    useLipaBidhaaVerification();

  const { mutate: resendOtp } = useLipaBidhaaResendOtp();

  const {
    secondsLeft: resendSecondsLeft,
    startTimer: startResendTimer,
    isActive: isResendCooldownActive,
  } = useResendTimer(60);

  const { isPolling, startPolling } = usePollTransaction();

  // Only fetch on Step 4 — polling is owned by usePollTransaction, and this
  // data is not needed until the result screen.
  const {
    data: transactionData,
    isLoading: isLoadingTransaction,
    isError: isTransactionError,
    refetch: refetchTransaction,
  } = useLipaBidhaaTransaction(currentStep >= 4 ? transactionUuid : null, {
    enablePolling: false,
  });

  const {
    control: otpControl,
    handleSubmit: handleOtpSubmit,
    watch: watchOtp,
  } = useForm({ defaultValues: { otp: "" } });

  const otpValue = watchOtp("otp") ?? "";

  const navigate = useNavigate();

  const handleOtpTimeout = useCallback(() => {
    navigate("/lipa-bidhaa", {
      state: { lipaBidhaaBannerReason: "otp_session_expired" },
    });
  }, [navigate]);

  const { resetTimer: resetOtpTimer } = useOtpInactivityTimer({
    otpValue,
    isAuthorizing,
    isPolling,
    isActive: currentStep === 3,
    onTimeout: handleOtpTimeout,
  });

  const liveAlias = watch("alias");
  const liveAmount = watch("amount");
  const liveAccountNumber = watch("accountNumber");

  const paymentTypeLabel = getDisplayValue(
    PAYMENT_TYPE_HEADER[paymentType]?.title,
  );
  const aliasLabel =
    PAYMENT_TYPE_FIELD_CONFIG[paymentType]?.aliasLabel ?? "Reference";
  const merchantVerificationStatus = getMerchantVerificationStatus(
    aliasVerificationStatus,
  );
  const paymentRail = getDisplayValue(PAYMENT_TYPE_CONFIG[paymentType]?.rail);
  const formattedAmount = formatKES(liveAmount);
  const formattedTransactionFee = formatKES(transactionFee);
  const formattedWalletBalance = formatKES(walletBalance);

  const summaryCardProps = buildSummaryCardProps({
    paymentTypeLabel,
    aliasLabel,
    liveAlias,
    businessName,
    merchantVerificationStatus,
    formattedAmount,
    formattedTransactionFee,
    formattedWalletBalance,
    currentStep,
    paymentRail,
  });

  const handleStep1Next = async () => {
    const isValid = await trigger(watchFields);
    if (!isValid) return;

    if (aliasVerificationStatus !== ALIAS_VERIFICATION_STATUS.VERIFIED) {
      setFieldError("alias", getVerifyErrorMessage(paymentType));
      return;
    }

    fetchCharges(
      buildTransactionPayload({
        paymentType,
        alias: liveAlias,
        accountNumber: liveAccountNumber,
        amount: liveAmount,
      }),
      {
        onSuccess: (data) => {
          useLipaBidhaaStore.getState().setTransactionCharges({
            transactionFee: data.totalCharge,
            totalAmount: data.totalAmount,
          });
          useLipaBidhaaStore.getState().setWalletBalance(data.newBalance);
          useLipaBidhaaStore.getState().setCurrentStep(2);
        },
        onError: (error) => {
          toastService.error(extractAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleStep2Back = () => {
    useLipaBidhaaStore.getState().setCurrentStep(1);
  };

  const handleStep2Next = () => {
    const { verifiedAlias, accountNumber, transactionAmount } =
      useLipaBidhaaStore.getState();

    submitPayment(
      buildTransactionPayload({
        paymentType,
        alias: verifiedAlias,
        accountNumber,
        amount: transactionAmount,
      }),
      {
        onSuccess: (data) => {
          useLipaBidhaaStore.getState().setTransactionUuid(data.id);
          useLipaBidhaaStore.getState().setOtpRecipient(data.phoneNumber);
          startResendTimer();
          useLipaBidhaaStore.getState().setCurrentStep(3);
        },
        onError: () => {
          toastService.error("Unable to initiate payment. Please try again.");
        },
      },
    );
  };

  const handleResendOtp = () => {
    const { transactionUuid: uuid } = useLipaBidhaaStore.getState();
    if (!uuid) return;
    resetOtpTimer();
    startResendTimer();
    resendOtp(uuid, {
      onError: () => {
        toastService.error("Failed to resend code. Please try again.");
      },
    });
  };

  const handleStep3Submit = ({ otp }) => {
    verifyOtp(
      { otp },
      {
        onSuccess: (data) => {
          useLipaBidhaaStore.getState().setTransactionResult(data);
          startPolling();
        },
        onError: (err) => {
          if (!err.response) {
            toastService.error(
              "Request failed. Please check your connection and try again.",
            );
            return;
          }
          const message = extractAxiosErrorMessage(err);
          if (isInvalidOtpMessage(message)) {
            toastService.error(
              "Invalid code entered. Please check and try again.",
            );
            return;
          }
          if (message.toLowerCase().includes("validation blocked")) {
            applyOtpBlock(err.response, useLipaBidhaaOtpBlockStore);
            navigate("/lipa-bidhaa");
            return;
          }
          useLipaBidhaaStore.getState().setTransactionFailed(message);
          useLipaBidhaaStore.getState().setCurrentStep(4);
        },
      },
    );
  };

  return {
    currentStep,
    control,
    walletBalance,
    isBalanceError,
    handleStep1Next,
    handleStep2Back,
    handleStep2Next,
    isRequestingPayment,
    summaryCardProps,
    isLoadingCharges,
    otpControl,
    handleOtpSubmit,
    otpValue,
    otpRecipient,
    handleStep3Submit,
    isAuthorizing,
    handleResendOtp,
    resendSecondsLeft,
    isResendCooldownActive,
    isPolling,
    transactionUuid,
    transactionData,
    isLoadingTransaction,
    isTransactionError,
    refetchTransaction,
    paymentType,
    businessName,
    accountNumber: storedAccountNumberForResult,
    verifiedAlias,
    walletBalanceSnapshot: storedWalletBalance,
    remarks,
    draftTransactionAmount,
    draftTransactionFee: transactionFee,
  };
}
